import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { BookOpen, Star, ShoppingCart, ChevronRight, Layers, Facebook, Twitter, Instagram, Linkedin } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { useCart } from "@/context/CartContext";
import StoreShell from "@/components/StoreShell";
import { Reveal } from "@/components/pharmacy/hooks";
import { ManagedReviews, ManagedInstagram } from "@/components/storefront/HomeSections";
import { INK, InkTitle } from "@/components/books/BootinParts";
import { authorOf } from "@/components/books/BookParts";
import { hasPriceRange } from "@/lib/variants";
import { homeSets } from "@/lib/homesets";
import { inr, rating, reviews } from "@/lib/storeutils";

const PANELS = ["#fbaaac", "#e5aed0", "#97d3e0", "#f7d58b", "#b9e0bd", "#c9b8ec"];
const pct = (p) => (p.compare_at_price ? Math.round((1 - p.price / p.compare_at_price) * 100) : 0);
const needsOptions = (p) => p.variants?.length > 0 || p.variant_items?.length > 0;

export default function InkHome() {
  const s = useStore();
  const cart = useCart();
  const [params] = useSearchParams();
  const q = (params.get("q") || "").toLowerCase();
  useEffect(() => { if (q && !s.loading) document.getElementById("shop")?.scrollIntoView({ behavior: "smooth" }); }, [q, s.loading]);

  const all = s.products;
  const featured = useMemo(() => all.filter((p) => p.featured), [all]);
  const authors = useMemo(() => {
    const m = new Map();
    all.forEach((p) => { const a = authorOf(p); if (a) m.set(a, [...(m.get(a) || []), p]); });
    return [...m.entries()].sort((a, b) => b[1].length - a[1].length).map(([name, books]) => ({ name, books }));
  }, [all]);

  if (s.loading || !s.store) return <div className="min-h-screen flex items-center justify-center"><span className="w-10 h-10 rounded-full border-4 border-slate-200 border-t-rose-400 animate-spin" /></div>;
  if (s.error) return <div className="min-h-screen flex items-center justify-center text-slate-500">{s.error}</div>;

  const has = all.length > 0;
  const lead = featured.length ? featured : all;
  const cats = s.categories;
  const catTiles = cats.map((name) => ({ name, count: all.filter((p) => p.category === name).length }));
  // three shelves with different products: new arrivals, featured, on sale (see lib/homesets.js)
  const sets = homeSets(all, { fresh: 8, featured: 4, sale: 6 });
  const renderCard = (p) => <InkCard p={p} s={s} cart={cart} />;


  const shown = all.filter((p) => p.name.toLowerCase().includes(q) || authorOf(p).toLowerCase().includes(q));
  return (
    <StoreShell>
      {q ? (
        <section id="shop" className="max-w-6xl mx-auto px-5 py-12" data-testid="ink-search-results">
          <InkTitle>Results for “{q}”</InkTitle>
          {shown.length === 0 ? <div className="text-center py-16 text-slate-500">No books found.</div> : (
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-6" data-testid="storefront-products">{shown.map((p) => <InkCard key={p.id} p={p} s={s} cart={cart} />)}</div>
          )}
        </section>
      ) : (
        <>
          <Slider s={s} items={lead.slice(0, 9)} cart={cart} />
          {catTiles.length > 0 && <CategoryStrip s={s} tiles={catTiles} />}
          {sets.fresh.length > 0 && (
            <section id="new-arrivals" className="max-w-6xl mx-auto px-5 py-14 grid lg:grid-cols-[1fr_250px] gap-10">
              <div>
                <InkTitle>New arrivals</InkTitle>
                <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-9" data-testid="storefront-products">
                  {sets.fresh.map((p, k) => <Reveal key={p.id} delay={(k % 4) * 70} kind="rv-zoom"><InkCard p={p} s={s} cart={cart} /></Reveal>)}
                </div>
              </div>
              {authors[0] && <AuthorCard s={s} author={authors[0]} />}
            </section>
          )}
          {sets.featured.length > 0 && (
            <section id="bestsellers" className="max-w-6xl mx-auto px-5 pb-14">
              <InkTitle center>Featured</InkTitle>
              <div className="mt-9 grid grid-cols-2 md:grid-cols-4 gap-6">
                {sets.featured.map((p, k) => <Reveal key={p.id} delay={k * 80} kind="rv-zoom"><InkCard p={p} s={s} cart={cart} big /></Reveal>)}
              </div>
            </section>
          )}
          {has && <EventBanner s={s} items={all} />}
          {authors[0] && <AuthorSpotlight s={s} author={authors[0]} />}
          {sets.sale.length > 0 && <OnSale s={s} cart={cart} items={sets.sale} cats={cats} />}
          <ManagedReviews s={s} layout="bookish" fallback={<Reviews s={s} />} />
          <ManagedInstagram s={s} layout="bookish" fallback={<InstaGrid s={s} />} />
        </>
      )}
    </StoreShell>
  );
}

/* ---------- Hero: three pastel panels, books pop out of the bottom ---------- */
function useIsMobile() {
  const q = "(max-width: 767px)";
  const [m, setM] = useState(() => typeof window !== "undefined" && window.matchMedia(q).matches);
  useEffect(() => { const mq = window.matchMedia(q); const f = () => setM(mq.matches); f(); mq.addEventListener("change", f); return () => mq.removeEventListener("change", f); }, []);
  return m;
}

function Slider({ s, items, cart }) {
  const { c } = s;
  const mobile = useIsMobile();
  // desktop: two wide panels per page (room for a big book); phones: one panel at a time, with the book on top and the text and button below it
  const per = mobile ? 1 : 2;
  const pages = Math.max(1, Math.ceil(items.length / per));
  const [page, setPage] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => { setPage(0); }, [per]);
  useEffect(() => { if (paused || pages < 2) return undefined; const t = setInterval(() => setPage((x) => (x + 1) % pages), 6500); return () => clearInterval(t); }, [paused, pages]);
  // always `per` panels when there are enough books: wrap around to the start on the last page
  const slice = items.length >= per ? Array.from({ length: per }, (_, k) => items[(page * per + k) % items.length]) : items;
  if (!slice.length) return null;
  return (
    <section className="relative" data-testid="ink-hero" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div key={`${per}-${page}`} className="grid" style={{ gridTemplateColumns: `repeat(${slice.length}, minmax(0,1fr))` }}>
        {slice.map((p, k) => {
          const author = authorOf(p, s.store.name);
          return (
            <div key={p.id} className="ink-cell rx-slide-up" style={{ animationDelay: `${k * 90}ms` }}>
            <div className="ink-panel relative px-6 pt-9 md:pt-10 pb-9 md:pb-6" style={{ background: PANELS[(page * per + k) % PANELS.length] }}>
              {/* the book: above the text on phones, beside it (and popping out of the panel) on larger screens */}
              <div className="ink-book relative z-20 mx-auto mb-6 w-[170px]">
                <Link to={`/store/${s.slug}/product/${p.id}`} className="bk-soft bk-soft-hero w-full" style={{ aspectRatio: "2/3" }}>
                  {p.image ? <img src={p.image} alt={p.name} /> : <span className="flex w-full h-full items-center justify-center bg-white"><BookOpen size={40} className="text-slate-300" /></span>}
                  {p.compare_at_price ? <span className="bk-tag">Sale</span> : null}
                </Link>
              </div>
              <div className="relative z-10 text-white text-center md:text-left">
                <Link to={`/store/${s.slug}/product/${p.id}`}><h2 className="text-[22px] md:text-[21px] font-bold leading-snug line-clamp-2 md:min-h-[58px]" style={{ fontFamily: INK.serif }}>{p.name}</h2></Link>
                <div className="mt-2 text-[13px] italic opacity-95">by {author}</div>
                <div className="mt-3 flex gap-0.5 justify-center md:justify-start">{[...Array(5)].map((_, i) => <Star key={i} size={13} fill={i < Math.round(rating(p.id)) ? "#fff" : "none"} color="#fff" />)}</div>
                <p className="mt-4 md:mt-5 text-[12.5px] leading-relaxed opacity-95 line-clamp-4 md:min-h-[81px]">{p.description || `Available now at ${s.store.name}.`}</p>
                <button onClick={() => { if (needsOptions(p)) { window.location.href = `/store/${s.slug}/product/${p.id}`; return; } cart.add(p, null, 1); toast.success("Added to cart"); }} disabled={p.stock <= 0} data-testid={`ink-hero-add-${p.id}`}
                  className="rx-btn mt-6 md:mt-8 inline-block bg-white px-6 py-3 text-[12px] font-semibold shadow-md disabled:opacity-60" style={{ color: INK.ink, fontFamily: INK.serif }}>{needsOptions(p) ? "Select options" : p.stock <= 0 ? "Sold out" : "Add To Cart"}</button>
              </div>
            </div>
            </div>
          );
        })}
      </div>
      <div className="h-[70px] md:h-[170px] flex items-end justify-center pb-5 gap-2">
        {Array.from({ length: pages }).map((_, k) => <button key={k} aria-label={`Slide ${k + 1}`} onClick={() => setPage(k)} className="w-2.5 h-2.5 rounded-full transition-all" style={{ background: c.primary, opacity: page === k ? 1 : 0.35, transform: page === k ? "scale(1.15)" : "none" }} />)}
      </div>
    </section>
  );
}

/* ---------- Category strip ---------- */
function CategoryStrip({ s, tiles }) {
  return (
    <section id="categories" style={{ background: INK.grey }}>
      <div className="max-w-6xl mx-auto px-5 py-7 grid grid-cols-2 sm:flex sm:flex-wrap gap-x-4 gap-y-5 sm:gap-x-8" data-testid="ink-category-strip">
        {tiles.map((t, k) => (
          <Reveal key={t.name} delay={k * 60} className="min-w-0">
            <Link to={`/store/${s.slug}/shop?category=${encodeURIComponent(t.name)}`} className="group flex items-center gap-3 sm:gap-4 sm:min-w-[170px]" data-testid={`storefront-cat-tile-${t.name}`}>
              <span className="w-[46px] h-[46px] sm:w-[52px] sm:h-[52px] shrink-0 rounded-full bg-white border flex items-center justify-center transition-all group-hover:scale-105" style={{ borderColor: INK.line, color: INK.ink }}><Layers size={20} strokeWidth={1.5} /></span>
              <span className="min-w-0"><span className="block text-[15px] sm:text-[17px] font-bold break-words group-hover:text-[color:var(--ink-accent)] transition-colors" style={{ fontFamily: INK.serif, "--ink-accent": s.c.primary }}>{t.name}</span><span className="block text-[12px] mt-0.5" style={{ color: INK.muted }}>{t.count} Book{t.count === 1 ? "" : "s"}</span></span>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ---------- Author side card (next to New arrivals) ---------- */
function AuthorCard({ s, author }) {
  const { c } = s;
  const cats = [...new Set(author.books.map((b) => b.category).filter(Boolean))].slice(0, 4).join(", ");
  const cover = author.books.find((b) => b.image);
  return (
    <aside data-testid="ink-author-card" className="text-center">
      <InkTitle className="text-left">Author</InkTitle>
      <Reveal kind="rv-zoom" className="mt-8">
        <Link to={cover ? `/store/${s.slug}/product/${cover.id}` : `/store/${s.slug}/shop`} className="bk-soft overflow-hidden" style={{ background: INK.grey, aspectRatio: "1/1.1" }}>
          {cover ? <img src={cover.image} alt={author.name} /> : <span className="flex w-full h-full items-center justify-center text-[64px] font-bold" style={{ fontFamily: INK.serif, color: c.primary }}>{author.name[0]}</span>}
        </Link>
        <div className="mt-5 text-[19px]" style={{ fontFamily: INK.serif }}>{author.name}</div>
        {cats && <div className="mt-2 text-[12px]" style={{ color: INK.muted }}>{cats}</div>}
        <p className="mt-5 text-[12.5px] leading-relaxed" style={{ color: "#777" }}>{author.books.length} title{author.books.length === 1 ? "" : "s"} on our shelves, including {author.books.slice(0, 2).map((b) => `“${b.name}”`).join(" and ")}.</p>
        <div className="mt-5 flex justify-center gap-4" style={{ color: INK.ink }}>{[Facebook, Twitter, Instagram].map((I, i) => <I key={i} size={13} />)}</div>
        <span className="block mx-auto mt-6 h-px w-[54px]" style={{ background: c.primary }} />
      </Reveal>
    </aside>
  );
}

/* ---------- Event banner (soft parallax) ---------- */
function EventBanner({ s, items }) {
  const { c, slug } = s;
  const coupon = s.coupons[0];
  const best = Math.max(0, ...items.map(pct));
  return (
    <section className="relative overflow-hidden bg-[position:85%_bottom] md:bg-[position:70%_center]" data-testid="ink-event-banner"
      style={{ backgroundImage: "url(/themes/inkwell-banner.webp)", backgroundSize: "cover", backgroundColor: "#eaf4f4" }}>
      {/* text sits on the calm left/centre of the picture, clear of the books on the right */}
      <Reveal className="relative max-w-6xl mx-auto px-5 pt-10 pb-[230px] md:py-36">
        <div className="md:w-[58%] text-center rounded-lg bg-white/60 backdrop-blur-[2px] px-3 py-6 md:bg-transparent md:backdrop-blur-none md:p-0">
          <div className="text-[15px] uppercase" style={{ fontFamily: INK.serif, color: INK.ink }}>{coupon ? `Use code ${coupon.code}` : "Special offers"}</div>
          <h2 className="mt-4 text-[28px] md:text-[40px] font-bold leading-tight" style={{ fontFamily: INK.serif, color: INK.ink }}>{best > 0 ? `Best-selling books, discount up to ${best}%` : "Discover your next favourite read"}</h2>
          <Link to={`/store/${slug}/shop`} className="rx-btn mt-8 inline-block px-8 py-3 text-[12px] font-semibold text-white shadow-lg" style={{ background: c.primary, fontFamily: INK.serif }}>Explore Now</Link>
        </div>
      </Reveal>
    </section>
  );
}

/* ---------- Author spotlight ---------- */
function AuthorSpotlight({ s, author }) {
  const { c, slug } = s;
  const covers = author.books.filter((b) => b.image);
  const main = covers[0];
  return (
    <section className="max-w-6xl mx-auto px-5 py-16" data-testid="ink-author-spotlight">
      <InkTitle center>Author spotlight</InkTitle>
      <div className="mt-12 grid md:grid-cols-[1fr_340px_1fr] gap-8 items-center">
        <Reveal kind="rv-left" className="order-2 md:order-1 md:text-right">
          <div className="text-[13px]" style={{ color: INK.muted }}>Most titles in store</div>
          <div className="mt-2 text-[34px] font-bold" style={{ fontFamily: INK.serif, color: c.primary }}>{author.books.length}<span className="text-[14px] ml-1" style={{ color: INK.ink }}>book{author.books.length === 1 ? "" : "s"}</span></div>
          {main && <Link to={`/store/${slug}/product/${main.id}`} className="rx-btn mt-6 inline-block px-6 py-2.5 text-[12px] font-semibold text-white shadow-lg" style={{ background: c.primary, fontFamily: INK.serif }}>View Book</Link>}
        </Reveal>
        <Reveal kind="rv-zoom" className="order-1 md:order-2">
          <div className="bk-soft bk-soft-hero mx-auto w-full max-w-[340px] overflow-hidden" style={{ aspectRatio: "6/7", background: INK.grey }}>
            {main ? <img src={main.image} alt={main.name} /> : <span className="flex w-full h-full items-center justify-center text-[90px] font-bold" style={{ fontFamily: INK.serif, color: c.primary }}>{author.name[0]}</span>}
          </div>
        </Reveal>
        <Reveal kind="rv-right" className="order-3">
          <div className="text-[24px] font-bold" style={{ fontFamily: INK.serif }}>{author.name}</div>
          <ul className="mt-4 space-y-2 text-[13px]" style={{ color: "#666" }}>
            {author.books.slice(0, 4).map((b) => <li key={b.id} className="flex items-center justify-between gap-3 border-b pb-2" style={{ borderColor: INK.line }}><Link to={`/store/${slug}/product/${b.id}`} className="rx-link line-clamp-1" style={{ fontFamily: INK.serif }}>{b.name}</Link><b style={{ color: c.primary }}>{inr(b.price)}</b></li>)}
          </ul>
          <div className="mt-5 flex gap-4" style={{ color: INK.ink }}>{[Facebook, Twitter, Instagram, Linkedin].map((I, i) => <I key={i} size={14} />)}</div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- On Sale with category tabs ---------- */
function OnSale({ s, cart, items, cats }) {
  const { c } = s;
  const [tab, setTab] = useState("All");
  const tabs = ["All", ...cats.filter((x) => items.some((p) => p.category === x))].slice(0, 7);
  const list = (tab === "All" ? items : items.filter((p) => p.category === tab)).slice(0, 6);
  return (
    <section id="deals" style={{ background: INK.grey }}>
      <div className="max-w-6xl mx-auto px-5 py-16">
        <div className="flex items-end justify-between gap-6 flex-wrap border-b pb-3" style={{ borderColor: INK.line }}>
          <div className="relative pb-3"><h2 className="text-[26px] md:text-[28px] font-bold" style={{ fontFamily: INK.serif }}>On Sale</h2><span className="absolute left-0 -bottom-[13px] h-[2px] w-[54px]" style={{ background: c.primary }} /></div>
          <div className="flex gap-6 text-[14px] overflow-x-auto" style={{ fontFamily: INK.serif, scrollbarWidth: "none" }}>
            {tabs.map((t) => <button key={t} onClick={() => setTab(t)} className="relative pb-1 whitespace-nowrap transition-colors" style={{ color: tab === t ? c.primary : "#444" }}>{t}<span className="absolute left-0 -bottom-[14px] h-[2px] transition-all duration-300" style={{ width: tab === t ? "100%" : 0, background: c.primary }} /></button>)}
          </div>
        </div>
        <div key={tab} className="mt-9 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {list.map((p, k) => <div key={p.id} className="rx-slide-up bg-white p-5 shadow-sm" style={{ animationDelay: `${k * 70}ms` }}><InkCard p={p} s={s} cart={cart} row /></div>)}
        </div>
      </div>
    </section>
  );
}

/* ---------- Reader reviews (default homepage; vendors who use Homepage Management get their own testimonials instead) ---------- */
const SAMPLE_REVIEWS = [
  ["Joanne H.", "This is my favourite bookshop", "Love that you give books to those in need. Great service and I can find great value here, especially as I prefer hardcovers."],
  ["Viorel M.", "Quick and easy", "Prices are low, there are always promotions or discounts, and the website is easy to navigate."],
  ["Ciaren R.", "Excellent service", "The books were wrapped securely and arrived in pristine condition. I emailed after and got a prompt reply."],
  ["Margaret C.", "Best bookshop online", "I am so happy to have found a shop for unusual titles. The whole experience was phenomenal."],
];
function Reviews({ s }) {
  const { c } = s;
  return (
    <section id="reviews" className="max-w-6xl mx-auto px-5 pb-16" data-testid="ink-reviews">
      <InkTitle center>What our readers say</InkTitle>
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {SAMPLE_REVIEWS.map(([n, t, d], k) => (
          <Reveal key={n} delay={k * 90} className="bg-white border p-6" style={{ borderColor: INK.line }}>
            <div className="flex gap-0.5">{[...Array(5)].map((_, i) => <Star key={i} size={13} fill="#e9a23b" color="#e9a23b" />)}</div>
            <div className="mt-4 text-[16px] font-bold leading-snug" style={{ fontFamily: INK.serif }}>{t}</div>
            <p className="mt-3 text-[12.5px] leading-relaxed" style={{ color: "#777" }}>{d}</p>
            <div className="mt-5 pt-4 border-t text-[12px] font-semibold" style={{ borderColor: INK.line, color: c.primary }}>{n}</div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ---------- Instagram strip (default homepage; vendors who use Homepage Management get their own posts instead) ---------- */
function InstaGrid({ s }) {
  const imgs = s.products.filter((p) => p.image).slice(0, 6);
  if (imgs.length === 0) return null;
  return (
    <section className="max-w-6xl mx-auto px-5 py-16" data-testid="ink-instagram">
      <InkTitle center>Follow <b>@{s.store.name.replace(/\s+/g, "").toUpperCase()}</b> on Instagram</InkTitle>
      <div className="mt-9 grid grid-cols-3 md:grid-cols-6 gap-3">
        {imgs.map((p, k) => (
          <Reveal key={p.id} kind="rv-zoom" delay={k * 70}>
            <Link to={`/store/${s.slug}/product/${p.id}`} aria-label={p.name} className="group relative block aspect-square overflow-hidden">
              <img src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
              <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-white" style={{ background: "rgba(242,97,106,.65)" }}><Instagram size={22} /></span>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ---------- Product card ---------- */
export function InkCard({ p, s, cart, row, big }) {
  const { c, slug } = s;
  const off = pct(p);
  const hasVariants = needsOptions(p);
  const soldOut = p.stock <= 0;
  const [added, setAdded] = useState(false);
  const author = authorOf(p, s.store.name);
  const add = (e) => {
    e.preventDefault();
    if (hasVariants) { window.location.href = `/store/${slug}/product/${p.id}`; return; }
    cart.add(p, null, 1); toast.success("Added to cart"); setAdded(true); setTimeout(() => setAdded(false), 1200);
  };
  const price = (
    <div className="flex items-baseline gap-2">
      {hasPriceRange(p) && <span className="text-[11px]" style={{ color: INK.muted }}>From</span>}
      {p.compare_at_price ? <span className="text-[12px] line-through" style={{ color: "#555", fontFamily: INK.serif }}>{inr(p.compare_at_price)}</span> : null}
      <span className="text-[14px]" style={{ color: c.primary, fontFamily: INK.serif }}>{inr(p.price)}</span>
    </div>
  );
  const cover = (
    <Link to={`/store/${slug}/product/${p.id}`} className={`bk-soft overflow-hidden ${row ? "w-[104px] shrink-0 self-start bk-soft-hero" : ""}`} style={{ aspectRatio: row ? "2/3" : "3/4", background: "#eee" }}>
      {p.image ? <img src={p.image} alt={p.name} className="transition-transform duration-500 group-hover:scale-105" /> : <BookOpen size={32} className="m-auto mt-[45%] text-slate-300" />}
      {(off > 0 || soldOut) && <span className="bk-tag" style={{ background: soldOut ? "#888" : INK.ink }}>{soldOut ? "Sold out" : "Sale"}</span>}
      {!row && <button type="button" onClick={add} disabled={soldOut} data-testid={`storefront-add-to-cart-btn-${p.id}`} className="absolute inset-x-0 bottom-0 py-2.5 text-[11px] font-semibold text-white flex items-center justify-center gap-1.5 translate-y-full group-hover:translate-y-0 transition-transform duration-300 disabled:opacity-70" style={{ background: added ? "#16a34a" : c.primary }}>
        <ShoppingCart size={12} /> {added ? "Added" : hasVariants ? "Select options" : soldOut ? "Sold out" : "Add To Cart"}
      </button>}
    </Link>
  );
  return (
    <div data-testid={`storefront-product-${p.id}`} className={`group ${row ? "flex gap-5" : ""}`}>
      {cover}
      <div className={row ? "min-w-0 flex-1 pt-1" : "mt-4"}>
        <Link to={`/store/${slug}/product/${p.id}`}><h3 className={`leading-snug transition-colors group-hover:text-[color:var(--ink-accent)] ${row ? "text-[15px] line-clamp-2" : "text-[15px] line-clamp-1"}`} style={{ fontFamily: INK.serif, color: INK.ink, "--ink-accent": c.primary }}>{p.name}</h3></Link>
        <div className="mt-1.5 text-[12px]" style={{ color: INK.muted, fontFamily: INK.serif }}><i>by</i> {author}</div>
        {row && <div className="mt-2 flex gap-0.5">{[...Array(5)].map((_, i) => <Star key={i} size={10} fill={i < Math.round(rating(p.id)) ? "#e9a23b" : "none"} color={i < Math.round(rating(p.id)) ? "#e9a23b" : "#999"} />)}<span className="text-[10px] ml-1" style={{ color: INK.muted }}>({reviews(p.id)})</span></div>}
        <div className="mt-2.5">{price}</div>
        {row && <button type="button" onClick={add} disabled={soldOut} data-testid={`storefront-add-to-cart-btn-${p.id}`} className="rx-btn mt-4 px-5 py-2 text-[11px] font-semibold text-white disabled:opacity-60 inline-flex items-center gap-1.5" style={{ background: added ? "#16a34a" : c.primary, fontFamily: INK.serif }}>{added ? "Added" : hasVariants ? "Select options" : soldOut ? "Sold out" : "Add To Cart"}<ChevronRight size={11} /></button>}
      </div>
    </div>
  );
}
